import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AdvertFilters } from "../../../../types/advertList";
import { TIMESTAMP_DAY } from "../../../../config";
import { getAdverts } from "../../../../store/advert/useCases/getAdverts/action";
import { selectAdvert } from "../../../../store/advert/repository/selector";
import { useDebounce } from "../../../../hooks/useDebounce";
import toast from "react-hot-toast";
import { updateAdvert } from "../../../../store/advert/useCases/updateAdvert/action";
import { deleteAdvert } from "../../../../store/advert/useCases/deleteAdvert/action";
import { tokens } from "../../../../locales/tokens";
import { selectOrgStructure } from "../../../../store/orgStructure/repository/selector";
import { updateOrgStructure } from "../../../../store/orgStructure/useCases/updateOrgStructure/action";
import { uploadAdvertReport } from "../../../../store/advert/useCases/uploadReport/action";

export const useAdvertList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeOptions = [
    { id: 1, name: t(tokens.adverts.news), value: "News" },
    { id: 2, name: t(tokens.adverts.promo), value: "Promo" },
    { id: 3, name: t(tokens.adverts.event), value: "Event" },
  ];
  const findStatusLabel = (value: string) => {
    if (value === "Available") return t(tokens.documents.publish);
    if (value === "Hidden") return t(tokens.documents.notPublish);
    return "";
  };

  const findTypeLabel = (value: string) => {
    const type = typeOptions.find((type) => type.value === value);
    return type ? type.name : "";
  };

  const [filters, updateFilters] = useState<AdvertFilters>({
    page: Number(searchParams.get("page")) || 1,
    rowsPerPage: Number(searchParams.get("rowsPerPage")) || 30,
    query: searchParams.get("query") || "",
    tab: (searchParams.get("tab") as "Active" | "Deleted") || "Active",
    type: searchParams.get("type")
      ? {
          value: searchParams.get("type"),
          label: findTypeLabel(searchParams.get("type") as string),
        }
      : null,
    status: searchParams.get("status")
      ? {
          value: searchParams.get("status"),
          label: findStatusLabel(searchParams.get("status") as string),
        }
      : null,
    initiator: searchParams.get("initiator")
      ? {
          value: searchParams.get("initiator"),
          label: searchParams.get("initiator"),
        }
      : null,
    date: searchParams.get("date") ? Number(searchParams.get("date")) : null,
  });

  const {
    advertList,
    advertsCount,
    isLoading,
    deleteAdvertError,
    updateAdvertError,
  } = useSelector(selectAdvert);
  const [pageAmount, setPageAmount] = useState(0);
  const [updateClick, setUpdateClick] = useState(false);
  const [deleteClick, setDeleteClick] = useState(false);
  const { orgStructure } = useSelector(selectOrgStructure);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState(filters.query || "");

  useEffect(() => {
    const params: Record<string, string> = {
      page: String(filters.page),
      rowsPerPage: String(filters.rowsPerPage),
      query: filters.query || "",
      tab: filters.tab,
    };
    if (filters.type?.value) params.type = filters.type.value;
    if (filters.status?.value) params.status = filters.status.value;
    if (filters.initiator?.value) params.initiator = filters.initiator.value;
    if (filters.date) params.date = String(filters.date);

    setSearchParams(params);
  }, [filters]);

  useEffect(() => {
    dispatch(getAdverts(combineRequestBody()));
  }, [filters]);

  const combineRequestBody = () => {
    const body: any = { page: filters.page, perPage: filters.rowsPerPage };
    if (filters.query) body.theme = filters.query;
    if (filters.initiator) body.initiatorId = filters.initiator.value;
    if (filters.type) body.type = filters.type.value;
    if (filters.status) body.status = filters.status.value;
    if (filters.date) {
      const startDateString = new Date(filters.date).toISOString();
      body.updatedAtStart = startDateString;
      const endDateString = new Date(
        filters.date + TIMESTAMP_DAY,
      ).toISOString();
      body.updatedAtEnd = endDateString;
    }
    body.isDeleted = filters.tab === "Active" ? false : true;
    return body;
  };

  useEffect(() => {
    dispatch(updateOrgStructure());
  }, []);

  useEffect(() => {
    if (advertsCount) {
      setPageAmount(Math.ceil(advertsCount / filters.rowsPerPage));
    }
  }, [advertsCount]);

  const { debouncedWrapper: debouncedHandleAdvertSearch } = useDebounce(
    (value: string) => {
      const query = value || "";
      if (query.length !== 1) {
        updateFilters((prev) => ({ ...prev, query }));
        scrollTopList();
      }
    },
    400,
  );

  useEffect(() => {
    debouncedHandleAdvertSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    if (!isLoading && updateClick) {
      if (updateAdvertError) toast.error(updateAdvertError);
      else {
        toast.success(t(tokens.adverts.messages.advertUpdated));
        dispatch(getAdverts(combineRequestBody()));
      }
      setUpdateClick(false);
    }
  }, [updateAdvertError, isLoading]);

  useEffect(() => {
    if (!isLoading && deleteClick) {
      if (deleteAdvertError) toast.error(deleteAdvertError);
      else {
        filters.tab === "Active"
          ? toast.success(t(tokens.adverts.messages.advertRemoved))
          : toast.success(t(tokens.adverts.messages.advertRemovedHard));
        dispatch(getAdverts(combineRequestBody()));
      }
      setDeleteClick(false);
    }
  }, [deleteAdvertError, isLoading]);

  const handleSearchAdvertText = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchText(e.target.value);

  const handleTabsChange = (event: ChangeEvent<{}>, tab: string): void => {
    updateFilters((prev) => ({
      ...prev,
      tab: tab as AdvertFilters["tab"],
      page: 1,
    }));
    scrollTopList();
  };

  const changeType = useCallback((type: any) => {
    updateFilters((prev) => ({ ...prev, page: 1, type }));
    scrollTopList();
  }, []);

  const changeStatus = useCallback((status: any) => {
    updateFilters((prev) => ({ ...prev, page: 1, status }));
    scrollTopList();
  }, []);

  const handlePageChange = useCallback(
    (event: ChangeEvent<unknown>, page: number) => {
      updateFilters((prev) => ({ ...prev, page }));
      scrollTopList();
    },
    [],
  );

  const handleDateChange = useCallback((date: Date | null) => {
    const currentDate = date?.valueOf() || null;
    updateFilters((prev) => ({ ...prev, page: 1, date: currentDate }));
    scrollTopList();
  }, []);

  const resetFilters = useCallback(() => {
    updateFilters((prev) => ({
      ...prev,
      page: 1,
      rowsPerPage: 30,
      type: null,
      status: null,
      initiator: null,
      date: null,
      query: "",
    }));
    scrollTopList();
  }, []);

  const handleStatus = (id: number, status: string) => {
    const advertStatus = status === "Hidden" ? "Available" : "Hidden";
    dispatch(updateAdvert({ body: { id, status: advertStatus } }));
    setUpdateClick(true);
  };

  const handleDelete = (id: number, mode?: string) => {
    dispatch(deleteAdvert({ id, mode }));
    setDeleteClick(true);
  };

  const handleUploadReport = (id: number, name: string) => {
    dispatch(uploadAdvertReport({ id, name }));
  };

  const onSelectInitiator = (selectedKeysValue: string, node: any) => {
    if ("children" in node) return;
    updateFilters((prev) => ({ ...prev, page: 1, initiator: node }));
    scrollTopList();
  };

  const onChangeInitiator = () => {
    if (filters.initiator) {
      updateFilters((prev) => ({ ...prev, page: 1, initiator: null }));
    }
  };

  const scrollTopList = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  return {
    filters,
    searchText,
    handleTabsChange,
    handleSearchAdvertText,
    changeType,
    changeStatus,
    handlePageChange,
    advertList,
    orgStructure,
    handleDateChange,
    pageAmount,
    onSelectInitiator,
    onChangeInitiator,
    handleStatus,
    resetFilters,
    handleDelete,
    handleUploadReport,
    scrollRef,
  };
};
